import { ModuleName } from '../../../types/navigation';
import { MODULES } from '../../common/constants';
import { Menu, User, LogOut, Bell, Loader2 } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { IoIosArrowDown } from 'react-icons/io';
import { motion, AnimatePresence } from 'framer-motion';
import { MdGroups } from 'react-icons/md';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { usePmoNavigation } from 'hooks/usePmoNavigation';
import { useAppSelector } from '../../../store';
import {
  selectAuthUser,
  selectRoles,
  selectUserProfile,
} from '../../../store/slices/auth/auth.selector';
import { ROLE_LABEL_MAP, UserRole } from '../../../types/users';
import { getInitials } from 'utils/getInitials';
import { sanitizeDeepLink } from 'utils/notificationsHelpers';
import { useSettingsNavigation } from 'hooks/useSettingsNavigation';
import { useAuth } from 'hooks/useAuth';
import { useGetCurrentUserQuery } from 'service/user/useGetCurrentUserQuery';
import { NotificationDropdown } from '../../common/NotificationDropdown';
import {
  useNotificationsQuery,
  useNotificationStatsQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useDeleteNotificationMutation,
  useClearAllNotificationsMutation,
} from 'service/notifications/hooks';
import {
  useHrmNotificationsQuery,
  useHrmUnreadCountQuery,
  useHrmMarkAsReadMutation,
  useHrmMarkAllAsReadMutation,
  useHrmDeleteNotificationMutation,
  useHrmClearAllMutation,
} from 'service/hrm-notifications/hooks';
import {
  Notification,
  HrmNotification,
  NotificationOutcomeData,
} from 'types/notifications';
import GlobalSearchBar from 'components/common/GlobalSearchBar';
import SettingsMenu from './SettingsMenu';
import axiosInstance from 'service/axiosInstance';
import MissedCheckoutReviewModal, {
  MissedCheckoutData,
} from '../../Modals/MissedCheckoutReviewModal';
import NotificationOutcomeModal from '../../Modals/NotificationOutcomeModal';
import LeaveCancellationReviewModal from '../../Modals/LeaveCancellationReviewModal';
import {
  REVIEW_REQUIRED_NOTIFICATION_TYPES,
  isAttendanceNotificationType,
  buildAttendanceOutcomeData,
} from 'utils/notificationsHelpers';

interface HeaderProps {
  variant: UserRole | null;
  activeModule: ModuleName;
  setActiveModule?: (module: ModuleName) => void;
  onMobileMenuToggle?: () => void;
  // Whether the fixed sidebar (rendered separately, on top of this header) is
  // collapsed — used to align the module switcher's left edge with the
  // sidebar's right edge instead of a fixed offset from the logo.
  sidebarCollapsed?: boolean;
  // Some routes (e.g. /settings/*) render no sidebar at all. When false, the
  // switcher can't align to a sidebar edge and instead sits in-flow next to
  // the logo, like it did before the sidebar-aligned positioning was added.
  hasSidebar?: boolean;
}

const Header = ({
  variant,
  activeModule,
  setActiveModule,
  onMobileMenuToggle,
  sidebarCollapsed,
  hasSidebar = true,
}: HeaderProps) => {
  const { toProjects, buildProjectsUrl } = usePmoNavigation();
  const { buildManageUsersUrl } = useSettingsNavigation();
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [notificationDropdownOpen, setNotificationDropdownOpen] =
    useState(false);
  const notificationDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // ── Notification queries & mutations ──────────────────────────────────────
  const {
    data: notifInfiniteData,
    isLoading: isLoadingNotifications,
    refetch: refetchNotifications,
  } = useNotificationsQuery();

  const { data: statsData, refetch: refetchStats } =
    useNotificationStatsQuery();
  const pmoUnreadCount = statsData?.unread || 0;

  // ── HRM Notifications ─────────────────────────────────────────────────────
  const {
    data: hrmNotifInfiniteData,
    isLoading: isLoadingHrmNotifications,
    refetch: refetchHrmNotifications,
  } = useHrmNotificationsQuery();

  const { data: hrmUnreadCount = 0, refetch: refetchHrmUnreadCount } =
    useHrmUnreadCountQuery();

  const { mutate: hrmMarkAsRead } = useHrmMarkAsReadMutation();
  const { mutate: hrmMarkAllAsRead } = useHrmMarkAllAsReadMutation();
  const { mutate: hrmDeleteNotif } = useHrmDeleteNotificationMutation();
  const { mutate: hrmClearAll } = useHrmClearAllMutation();

  // Combined badge (PMO + HRM)
  const totalUnreadCount = pmoUnreadCount + hrmUnreadCount;

  // Sync-on-open: refetch both modules when dropdown is opened
  useEffect(() => {
    if (notificationDropdownOpen) {
      refetchNotifications();
      refetchStats();
      refetchHrmNotifications();
      refetchHrmUnreadCount();
    }
  }, [
    notificationDropdownOpen,
    refetchNotifications,
    refetchStats,
    refetchHrmNotifications,
    refetchHrmUnreadCount,
  ]);

  const { mutate: markAsRead } = useMarkAsReadMutation();
  const { mutate: markAllAsRead } = useMarkAllAsReadMutation();
  const { mutate: deleteNotif } = useDeleteNotificationMutation();
  const { mutate: clearAll } = useClearAllNotificationsMutation();

  // Flatten PMO pages into a single list
  const notifications: Notification[] =
    notifInfiniteData?.pages
      ?.flatMap((p) => p?.data || [])
      ?.filter(
        (n): n is Notification => !!n && typeof n === 'object' && !!n.createdAt,
      ) ?? [];

  // Flatten HRM pages into a single list
  const hrmNotifications: HrmNotification[] =
    hrmNotifInfiniteData?.pages
      ?.flatMap((p) => p?.data || [])
      ?.filter(
        (n): n is HrmNotification =>
          !!n && typeof n === 'object' && !!n.createdAt,
      ) ?? [];

  // Badge label: 1-9 shows number, 10+ shows "9+", 0 hides
  const badgeLabel =
    totalUnreadCount === 0
      ? null
      : totalUnreadCount >= 10
        ? '9+'
        : String(totalUnreadCount);

  const toggleNotificationDropdown = () => {
    setNotificationDropdownOpen((v) => !v);
    setProfileDropdownOpen(false);
  };

  const [isMissedCheckoutModalOpen, setIsMissedCheckoutModalOpen] =
    useState(false);
  const [selectedNotificationData, setSelectedNotificationData] =
    useState<MissedCheckoutData | null>(null);
  const [isOutcomeModalOpen, setIsOutcomeModalOpen] = useState(false);
  const [outcomeModalData, setOutcomeModalData] =
    useState<NotificationOutcomeData | null>(null);
  const [cancellationReviewUuid, setCancellationReviewUuid] = useState<string | null>(null);

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markAsRead(notification.uuid);
    }
    const typeStr = (notification.notificationType || '').toUpperCase();
    const isAttendanceNotif = isAttendanceNotificationType(
      typeStr,
      notification.referenceType as string,
    );

    if (isAttendanceNotif) {
      if (REVIEW_REQUIRED_NOTIFICATION_TYPES.includes(typeStr)) {
        setSelectedNotificationData({
          requestUuid: notification.referenceUuid || notification.uuid,
        });
        setIsMissedCheckoutModalOpen(true);
      } else {
        // Already-resolved outcome (approved/rejected/recorded) — just show what happened, nothing to review
        setOutcomeModalData(
          buildAttendanceOutcomeData(
            typeStr,
            notification.title,
            notification.notificationMessage || notification.title,
          ),
        );
        setIsOutcomeModalOpen(true);
      }
    } else if (notification.deepLink) {
      const targetPath =
        sanitizeDeepLink(
          notification.deepLink,
          authUser?.orgSlug || undefined,
        ) || notification.deepLink;
      if (notification.notificationType === 'TIME_LOGGED') {
        navigate(targetPath, {
          state: {
            activeTab: 'work-log',
            highlightUserUuid: notification.actorUuid,
          },
        });
      } else {
        navigate(targetPath);
      }
    }
    setNotificationDropdownOpen(false);
  };

  const handleRemove = (_uuid: string, e: React.MouseEvent) => {
    e.stopPropagation();
    deleteNotif(_uuid);
  };

  const handleMarkAllRead = () => {
    markAllAsRead();
  };

  const handleClearAll = () => {
    clearAll();
  };

  const location = useLocation();

  const authUser = useAppSelector(selectAuthUser);

  const userRoles = useAppSelector(selectRoles);

  const authProfile = useAppSelector(selectUserProfile);
  const { data: profile } = useGetCurrentUserQuery(!!authUser);

  const displayProfile = profile || authProfile;

  const getDisplayRoleForModule = (
    roles: UserRole[],
    module: ModuleName,
  ): string => {
    if (!roles || roles.length === 0) return 'User';

    if (roles.includes('ORG_OWNER')) return ROLE_LABEL_MAP['ORG_OWNER'];
    if (roles.includes('ORG_ADMIN')) return ROLE_LABEL_MAP['ORG_ADMIN'];

    if (module === 'HRMS') {
      if (roles.includes('HRM_ADMIN')) return ROLE_LABEL_MAP['HRM_ADMIN'];
      if (roles.includes('HRM_MANAGER')) return ROLE_LABEL_MAP['HRM_MANAGER'];
      if (roles.includes('HRM_EMPLOYEE')) return ROLE_LABEL_MAP['HRM_EMPLOYEE'];
    } else if (module === 'Project Management') {
      if (roles.includes('PMO_ADMIN')) return ROLE_LABEL_MAP['PMO_ADMIN'];
      if (roles.includes('PMO_MANAGER')) return ROLE_LABEL_MAP['PMO_MANAGER'];
      if (roles.includes('PMO_USER')) return ROLE_LABEL_MAP['PMO_USER'];
    } else if (module === 'CRM') {
      if (roles.includes('CRM_ADMIN')) return ROLE_LABEL_MAP['CRM_ADMIN'];
      if (roles.includes('CRM_MANAGER')) return ROLE_LABEL_MAP['CRM_MANAGER'];
      if (roles.includes('CRM_USER')) return ROLE_LABEL_MAP['CRM_USER'];
    }

    const priorityOrder: UserRole[] = [
      'HRM_ADMIN',
      'PMO_ADMIN',
      'CRM_ADMIN',
      'HRM_MANAGER',
      'PMO_MANAGER',
      'CRM_MANAGER',
      'HRM_EMPLOYEE',
      'PMO_USER',
      'CRM_USER',
      'EMPLOYEE',
    ];

    for (const role of priorityOrder) {
      if (roles.includes(role)) {
        return ROLE_LABEL_MAP[role] || role;
      }
    }

    return roles[0] ? ROLE_LABEL_MAP[roles[0]] || roles[0] : 'User';
  };

  // Route to module mapping
  const routeModuleMap = {
    pmo: 'Project Management' as ModuleName,
    hrms: 'HRMS' as ModuleName,
    crm: 'CRM' as ModuleName,
    finance: 'Finance' as ModuleName,
    overview: 'Overview' as ModuleName,
  };

  // Function to get the current active module based on route
  const getCurrentModuleFromRoute = (): ModuleName => {
    const currentPath = location.pathname;

    // Check if the current path matches any of our module routes
    for (const [routeSegment, moduleName] of Object.entries(routeModuleMap)) {
      if (currentPath.includes(routeSegment)) {
        return moduleName;
      }
    }

    if (currentPath.includes('/settings')) {
      return activeModule;
    }

    if (currentPath.includes('/notifications')) {
      return activeModule;
    }

    // Default to HRMS for routes like /dashboard, /employees, etc.
    return 'HRMS';
  };

  // Get the actual active module (either from route or prop)
  const currentActiveModule = getCurrentModuleFromRoute();
  // Manage Users is a cross-module admin screen — it must never show a
  // module-specific (or stale, leftover-from-the-previous-module) role
  // subtitle, regardless of which module the user navigated from.
  const isManageUsersRoute = /\/settings\/users(\/|$)/.test(location.pathname);
  const displayRole = isManageUsersRoute
    ? null
    : getDisplayRoleForModule(userRoles, currentActiveModule);

  // Both Manage Users and the account-settings Profile page live under the
  // generic /settings prefix, which AdminLayout always defaults to the HRMS
  // module regardless of the signed-in user's actual roles. getCurrentModuleFromRoute
  // then echoes that stale default back for any /settings path, so a user with
  // no real HRMS access (e.g. PM Admin) would otherwise get redirected away
  // from these cross-module screens by the HRMS-access effects below.
  const isCrossModuleSettingsRoute =
    isManageUsersRoute ||
    /\/settings\/user\/profile(\/|$)/.test(location.pathname);

  const pathParts = location.pathname.split('/');
  const isPmo = pathParts[2] === 'pmo';
  const pmoRoute = pathParts[3];

  // Update the parent state only when necessary to keep it in sync
  useEffect(() => {
    if (setActiveModule && activeModule !== currentActiveModule) {
      setActiveModule(currentActiveModule);
    }
  }, [currentActiveModule, activeModule, setActiveModule]);

  const isOrgAdmin = userRoles.some((role) =>
    ['ORG_ADMIN', 'ORG_OWNER'].includes(role),
  );
  const hasHrmsAccess =
    isOrgAdmin ||
    userRoles.some((role) =>
      ['HRM_ADMIN', 'HRM_MANAGER', 'HRM_EMPLOYEE', 'EMPLOYEE'].includes(role),
    );
  const hasPmoAccess =
    isOrgAdmin ||
    userRoles.some((role) =>
      ['PMO_ADMIN', 'PMO_MANAGER', 'PMO_USER', 'EMPLOYEE'].includes(role),
    );
  const hasCrmAccess =
    isOrgAdmin ||
    userRoles.some((role) =>
      ['CRM_ADMIN', 'CRM_MANAGER', 'CRM_USER'].includes(role),
    );

  // Redirect if they land on HRMS or CRM without access. Manage Users is
  // cross-module and gets misclassified as "HRMS" by getCurrentModuleFromRoute's
  // /settings fallback (it just echoes back whatever module the layout
  // defaulted to) — skip it here or a CRM/PM-only admin with no HRMS role
  // gets bounced away from a page they're actually authorized to see.
  useEffect(() => {
    if (isCrossModuleSettingsRoute) return;
    if (currentActiveModule === 'HRMS' && !hasHrmsAccess && hasPmoAccess) {
      toProjects();
    }
    if (currentActiveModule === 'CRM' && !hasCrmAccess) {
      if (hasHrmsAccess && authUser?.orgSlug) {
        navigate(`/${authUser.orgSlug}/hrms/dashboard`);
      } else if (hasPmoAccess) {
        toProjects();
      }
    }
  }, [
    currentActiveModule,
    hasHrmsAccess,
    hasPmoAccess,
    hasCrmAccess,
    authUser?.orgSlug,
    navigate,
    toProjects,
    isCrossModuleSettingsRoute,
  ]);

  const { logout } = useAuth();
  const [isSigningOut, setIsSigningOut] = useState(false);
  // Profile dropdown menu items
  const profileMenuItems = [
    {
      id: 'profile',
      label: 'Profile',
      icon: User,
      onClick: () => {
        navigate(`/${authUser?.orgSlug}/settings/user/profile`);
        setProfileDropdownOpen(false);
      },
    },
    // {
    //   id: "settings",
    //   label: "Settings",
    //   icon: IoIosSettings,
    //   onClick: () => {
    //     setProfileDropdownOpen(false);
    //   },
    // },
    // {
    //   id: 'manage-users',
    //   label: 'Manage Users',
    //   icon: MdGroups,
    //   onClick: () => {
    //     navigate('/overview/manage-users');
    //     setProfileDropdownOpen(false);
    //   },
    // },
    ...(userRoles.some((role) =>
      [
        'ORG_OWNER',
        'ORG_ADMIN',
        'PMO_ADMIN',
        'HRM_ADMIN',
        'HRM_MANAGER',
        'CRM_ADMIN',
      ].includes(role),
    )
      ? [
          {
            id: 'users',
            label: 'Manage Users',
            icon: MdGroups,
            onClick: () => {
              navigate(`${buildManageUsersUrl()}`);
              setProfileDropdownOpen(false);
            },
          },
        ]
      : []),
    // Org Settings has moved to the settings menu beside this one, where it
    // sits with the module settings it belongs next to. It keeps the same
    // Org Owner/Org Admin gate it had here.
    // {
    //   id: 'billing',
    //   label: 'Billing',
    //   icon: BsCreditCardFill,
    //   onClick: () => {
    //     setProfileDropdownOpen(false);
    //   },
    // },
    // {
    //   id: 'switch-account',
    //   label: 'Switch Account',
    //   icon: HiOutlineSwitchHorizontal,
    //   onClick: () => {
    //     setProfileDropdownOpen(false);
    //   },
    // },
    {
      id: 'sign-out',
      label: 'Sign Out',
      icon: LogOut,
      onClick: async () => {
        if (isSigningOut) return;
        setIsSigningOut(true);
        try {
          await axiosInstance.post('/organisation/log-out');
        } catch (error) {
          console.error('Failed to invalidate token:', error);
        }
        logout();
      },
    },
  ];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setProfileDropdownOpen(false);
      }
      if (
        notificationDropdownRef.current &&
        !notificationDropdownRef.current.contains(target)
      ) {
        setNotificationDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter modules based on role and current page
  // const getVisibleModules = () => {
  //   return MODULES.filter((module) => {
  //     // if (module.id === 'HRMS' && !hasHrmsAccess) return false;
  //     if (module.id === 'Project Management' && !hasPmoAccess) return false;
  //     // if (module.id === 'CRM' && !hasCrmAccess) return false;

  //     // // Hide Finance and Reports by default unless they are an org admin (or we can leave them hidden for now)
  //     // if ((module.id === 'Finance' || module.id === 'Reports') && !isOrgAdmin) return false;

  //     return true;
  //   });
  // };

  const getVisibleModules = () =>
    MODULES.filter((module) => {
      if (module.id === 'HRMS' && !hasHrmsAccess) return false;
      if (module.id === 'Project Management' && !hasPmoAccess) return false;
      if (module.id === 'CRM' && !hasCrmAccess) return false;
      return true;
    });

  const visibleModules = getVisibleModules();
  const isSingleModule = visibleModules.length <= 1;

  // Handle module click - navigate to the appropriate default route
  const handleModuleClick = (moduleId: ModuleName) => {
    setActiveModule?.(moduleId);

    // Navigate to the appropriate default route for each module
    switch (moduleId) {
      case 'Project Management':
        toProjects();
        break;
      case 'CRM':
        if (authUser?.orgSlug) {
          navigate(`/${authUser.orgSlug}/crm/contacts`);
        } else {
          navigate('/overview');
        }
        break;
      case 'HRMS':
      default:
        if (authUser?.orgSlug) {
          navigate(`/${authUser.orgSlug}/hrms/dashboard`);
        } else {
          navigate('/overview');
        }
        break;
    }
  };

  const moduleSwitcherButtons = visibleModules.map((module) => {
    const isActive = currentActiveModule === module.id;

    return (
      <button
        key={module.id}
        className={`px-4 py-[10px] rounded-lg transition-colors relative ${
          isActive
            ? 'bg-primary-variant text-neutral after:content-[""] after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:bg-gradient-to-r after:from-[#8B5CF6] after:to-[#3B82F6]'
            : 'hover:bg-primary-subtle text-gray-400'
        }`}
        onClick={() => handleModuleClick(module.id as ModuleName)}
      >
        {module.label}
      </button>
    );
  });

  const getLogoRoute = () => {
    // Always navigate to the home of the currently active module first.
    // This prevents HR-only users from being sent to PMO when they click the logo.
    switch (currentActiveModule) {
      case 'Project Management':
        if (hasPmoAccess) return buildProjectsUrl();
        break;
      case 'CRM':
        if (hasCrmAccess && authUser?.orgSlug)
          return `/${authUser.orgSlug}/crm/contacts`;
        break;
      case 'HRMS':
      default:
        if (hasHrmsAccess && authUser?.orgSlug)
          return `/${authUser.orgSlug}/hrms/dashboard`;
        break;
    }

    // Fallback: navigate to whichever module the user has access to.
    // Priority: HRMS → PMO → CRM → overview
    if (hasHrmsAccess && authUser?.orgSlug)
      return `/${authUser.orgSlug}/hrms/dashboard`;
    if (hasPmoAccess) return buildProjectsUrl();
    if (hasCrmAccess && authUser?.orgSlug)
      return `/${authUser.orgSlug}/crm/contacts`;
    return '/overview';
  };

  return (
    <header className="w-full fixed top-0 left-0 right-0 z-50 bg-primary-light shadow-sm border-b border-primary-subtle">
      <div className="max-w-screen mx-auto px-6 flex items-center justify-between h-[70px] relative">
        {/* Left section - Logo and mobile menu button */}
        {/* <div className="flex items-center gap-4">
          <button
            className="min-991:hidden text-gray-400"
            onClick={onMobileMenuToggle}
          >
            <Menu size={24} />
          </button>

          <Link to={getLogoRoute()} className="inline-flex items-center">
            <img
              src="/assets/worklap.svg"
              alt="worklap-logo"
              className="h-8 w-auto"
            />
          </Link>
        </div> */}

        {/* Center section - Module switcher (desktop) */}
        {/* <nav className="hidden min-991:flex space-x-2">
          {visibleModules.map((module) => (
            <button
              key={module.id}
              className={`px-4 py-[10px] rounded-lg transition-colors relative  ${currentActiveModule === module.id
                ? 'bg-primary-variant text-neutral after:content-[""] after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:bg-gradient-to-r after:from-[#8B5CF6] after:to-[#3B82F6]'
                : 'hover:bg-primary-subtle text-gray-400'
                }`}
              onClick={() => handleModuleClick(module.id as ModuleName)}
            >
              {module.label}
            </button>
          ))}
        </nav> */}

        {/* Left section - Logo, mobile menu, and module switcher */}
        <div className="flex items-center gap-4">
          <button
            className="min-991:hidden text-gray-400"
            onClick={onMobileMenuToggle}
          >
            <Menu size={24} />
          </button>

          <Link to={getLogoRoute()} className="inline-flex items-center">
            <img
              src="/assets/worklap.svg"
              alt="worklap-logo"
              className="h-8 w-auto"
            />
          </Link>

          {/* Pages with no sidebar at all (e.g. /settings/*) have nothing to
              align the switcher against, and a collapsed sidebar (64px) is
              narrower than the logo itself — aligning to its edge there would
              put the switcher right on top of the wordmark. Both cases fall
              back to sitting in-flow next to the logo. */}
          {!isSingleModule && (!hasSidebar || sidebarCollapsed) && (
            <nav className="hidden min-991:flex items-center">
              {moduleSwitcherButtons}
            </nav>
          )}
        </div>

        {/* Module switcher — positioned absolutely against this header (whose
            box starts at the same x=0 as the fixed sidebar), with its left
            edge set to the sidebar's expanded width so it starts exactly where
            the sidebar stops. Only valid when an EXPANDED sidebar exists on
            this page — see the in-flow fallback above for the other cases. */}
        {!isSingleModule && hasSidebar && !sidebarCollapsed && (
          <nav className="hidden min-991:flex items-center absolute top-1/2 -translate-y-1/2 left-64">
            {moduleSwitcherButtons}
          </nav>
        )}

        {/* Right section - Search, notifications, profile */}
        <div className="flex items-center space-x-4">
          {currentActiveModule !== 'HRMS' && currentActiveModule !== 'CRM' && (
            <GlobalSearchBar />
          )}

          {/* Organisation and per-module settings. It shows itself only to
              someone with something to configure, so no gating is needed here. */}
          <SettingsMenu />

          {/* Notification dropdown container. The dropdown only carries PMO and
              HRMS notifications, so in CRM the bell opens on nothing — it is
              left off that module until CRM has notifications of its own. */}
          {currentActiveModule !== 'CRM' && (
          <div
            className="relative flex items-center"
            ref={notificationDropdownRef}
          >
            <button
              onClick={toggleNotificationDropdown}
              className={`relative p-2 rounded-full hover:bg-white/5 transition-all duration-200 ${
                notificationDropdownOpen
                  ? 'text-white bg-white/10'
                  : 'text-neutral-medium hover:text-neutral'
              }`}
            >
              <Bell size={22} />
              {badgeLabel && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-[#EF4444] rounded-full flex items-center justify-center text-[9px] font-bold text-white ring-2 ring-primary-light px-1">
                  {badgeLabel}
                </span>
              )}
            </button>

            {/* Notification Dropdown */}
            <NotificationDropdown
              isOpen={notificationDropdownOpen}
              onClose={() => setNotificationDropdownOpen(false)}
              notifications={notifications}
              hrmNotifications={hrmNotifications}
              unreadCount={pmoUnreadCount}
              hrmUnreadCount={hrmUnreadCount}
              isLoading={isLoadingNotifications}
              isLoadingHrm={isLoadingHrmNotifications}
              hasPmoAccess={hasPmoAccess}
              hasHrmsAccess={hasHrmsAccess}
              onViewAll={() => {
                setNotificationDropdownOpen(false);
                navigate(`/${authUser?.orgSlug}/notifications`);
              }}
              onHrmViewAll={() => {
                setNotificationDropdownOpen(false);
                navigate(`/${authUser?.orgSlug}/hrms/notifications`);
              }}
              onRemove={handleRemove}
              onMarkAllRead={handleMarkAllRead}
              onClearAll={handleClearAll}
              onNotificationClick={handleNotificationClick}
              onHrmRemove={(uuid) => hrmDeleteNotif(uuid)}
              onHrmMarkAllRead={() => hrmMarkAllAsRead()}
              onHrmClearAll={() => hrmClearAll()}
              onHrmNotificationClick={(n) => {
                if (!n.isRead) hrmMarkAsRead(n.uuid);

                const typeStr = (n.notificationType || '').toUpperCase();
                const titleStr = (n.title || '').toLowerCase();
                const msgStr = (n.notificationMessage || '').toLowerCase();
                const isAttendanceNotif = isAttendanceNotificationType(
                  typeStr,
                  n.referenceType,
                );

                if (isAttendanceNotif) {
                  if (REVIEW_REQUIRED_NOTIFICATION_TYPES.includes(typeStr)) {
                    setSelectedNotificationData({
                      requestUuid: n.referenceUuid || n.uuid,
                    });
                    setIsMissedCheckoutModalOpen(true);
                  } else {
                    // Already-resolved outcome (approved/rejected/recorded) — just show what happened, nothing to review
                    setOutcomeModalData(
                      buildAttendanceOutcomeData(
                        typeStr,
                        n.title,
                        n.notificationMessage || n.title,
                      ),
                    );
                    setIsOutcomeModalOpen(true);
                  }
                } else if (n.referenceType === 'LEAVE_CANCELLATION_REQUEST' && n.referenceUuid) {
                  // No deepLink on these — referenceUuid is the cancellation
                  // request itself, reviewed in place.
                  setCancellationReviewUuid(n.referenceUuid);
                } else if (n.referenceType === 'LEAVE_REQUEST' && authUser?.orgSlug && n.referenceUuid) {
                  // The backend's deepLink for leave notifications is a raw
                  // API path (/people/leave-requests/{uuid}), not a frontend
                  // route — navigating to it 404s past the auth guard into
                  // /unauthorized. Build the real route directly instead.
                  navigate(`/${authUser.orgSlug}/hrms/leave/requests/${n.referenceUuid}`);
                } else if (n.deepLink) {
                  const targetPath =
                    sanitizeDeepLink(
                      n.deepLink,
                      authUser?.orgSlug || undefined,
                    ) || n.deepLink;
                  navigate(targetPath);
                } else if (authUser?.orgSlug) {
                  const orgSlug = authUser.orgSlug;
                  if (
                    typeStr === 'ONBOARDING_PROFILE_SUBMITTED' ||
                    typeStr === 'ONBOARDING_PROFILE_RESUBMITTED'
                  ) {
                    navigate(
                      `/${orgSlug}/hrms/onboarding-queue/${n.referenceUuid}/review`,
                    );
                  } else if (
                    typeStr.includes('ONBOARDING') ||
                    typeStr.includes('PROFILE') ||
                    titleStr.includes('onboarding') ||
                    msgStr.includes('onboarding')
                  ) {
                    navigate(`/${orgSlug}/account-setup/employee-onboarding`);
                  } else if (typeStr === 'NEW_TEAM_MEMBER_JOINED') {
                    navigate(`/${orgSlug}/hrms/employees`);
                  }
                }

                setNotificationDropdownOpen(false);
              }}
            />
          </div>
          )}



          {/* Profile dropdown container */}
          <div className="relative" ref={dropdownRef}>
            <button
              className="flex items-center  space-x-2 hover:opacity-80 transition-opacity"
              onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
            >
              <div className="w-8 h-8 text-sm mr-2 flex items-center justify-center bg-blue-gray-700 rounded-full overflow-hidden">
                {profile?.profileImageURL ? (
                  <img
                    src={profile.profileImageURL}
                    alt="User avatar"
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  getInitials(
                    `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`,
                  )
                )}
              </div>
              <div className="hidden min-991:block">
                <div className="text-sm font-medium">
                  {displayProfile
                    ? `${displayProfile.firstName ?? ''} ${displayProfile.lastName ?? ''}`.trim()
                    : '...'}
                </div>
                {displayRole && (
                  <div className="text-xs text-neutral-medium">
                    {displayRole}
                  </div>
                )}
              </div>
              <motion.div
                animate={{ rotate: profileDropdownOpen ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <IoIosArrowDown size={20} className="text-neutral-medium" />
              </motion.div>
            </button>

            {/* Profile Dropdown Menu */}
            <AnimatePresence>
              {profileDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  className="absolute top-full right-0 mt-2 w-64 bg-primary-light rounded-xl shadow-lg border border-[#3A3D45] overflow-hidden z-50"
                >
                  {/* Menu items */}
                  <div className="py-2">
                    {profileMenuItems.map((item, index) => {
                      const isSignOutPending =
                        item.id === 'sign-out' && isSigningOut;
                      return (
                        <motion.button
                          key={item.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05, duration: 0.15 }}
                          onClick={item.onClick}
                          disabled={isSignOutPending}
                          className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-[#3A3D45] transition-colors group disabled:opacity-60 disabled:cursor-not-allowed ${
                            index !== 0 && 'border-t border-primary-subtle'
                          }`}
                        >
                          {isSignOutPending ? (
                            <Loader2
                              size={18}
                              className="animate-spin text-gray-400"
                            />
                          ) : (
                            <item.icon
                              size={18}
                              className="text-gray-400 group-hover:text-white transition-colors"
                            />
                          )}
                          <span className="text-sm  group-hover:text-white transition-colors">
                            {isSignOutPending ? 'Signing out...' : item.label}
                          </span>
                        </motion.button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
      <MissedCheckoutReviewModal
        isOpen={isMissedCheckoutModalOpen}
        onClose={() => setIsMissedCheckoutModalOpen(false)}
        data={selectedNotificationData}
      />
      <NotificationOutcomeModal
        isOpen={isOutcomeModalOpen}
        onClose={() => setIsOutcomeModalOpen(false)}
        data={outcomeModalData}
      />
      <LeaveCancellationReviewModal
        isOpen={!!cancellationReviewUuid}
        onClose={() => setCancellationReviewUuid(null)}
        cancellationRequestUuid={cancellationReviewUuid}
      />
    </header>
  );
};

export default Header;
